import * as React from 'react';
import {identity} from '@packages/utilities/fp/utils';
import {
  Result,
  Plugins,
  TypeImplementation,
  withSystem,
  setType,
  getMeta,
  withMeta,
  MetaAttributes,
} from '@packages/systems/wfdl';
import {
  TTypeConstructor,
  TEmptyRecord,
  TUnion,
  TText,
  EUnion,
  ERecord,
  ECase,
  EText,
  TList,
  functionOf,
  EFunction,
} from '@packages/systems/wfdl-ast';
import {
  type Qualified,
  qualify,
  typeName,
  pluginName,
  iconName,
} from '@packages/systems/wfdl-Qualified';
import {tyfun, forall, a, $, _$, func} from '@packages/utilities/wfdl-helpers';
import {
  ExpressionEditor,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';
import {FieldSwitchIcon, TextInput, Text} from '@packages/systems/spring';

import {CheckboxAliasType} from './CheckboxAlias';
import {ConditionValueAliasType} from './ConditionValueAlias';
import {
  ContextualExpression,
  ContextualExpressions,
  getAnn,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {operand} from '@packages/systems/plugins/ConditionsOperand';
import {DataTypeSummary} from '@packages/systems/ExpressionEditor-utils-ui';
import {
  TPredicate,
  toBooleanTag,
  TBoolean,
  BooleanTag,
  predicate,
  True,
  False,
  parseLabelsFromMeta,
} from '@packages/systems/plugins-utils/Boolean';

const unionType = TUnion({
  True: TEmptyRecord,
  False: TEmptyRecord,
});

const booleanPluginName = pluginName('Boolean');
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const name = <nameType extends unknown>(n: nameType): Qualified<nameType> =>
  qualify(booleanPluginName, n);

const TConditionValue = TTypeConstructor(name(typeName('ConditionValue')));

const BooleanMetaUI = ({
  meta,
  onChange,
}: {
  meta: MetaAttributes | null;
  onChange: (arg1: MetaAttributes) => void;
}) => {
  const {trueLabel, falseLabel} = parseLabelsFromMeta(meta);

  const [localTrueLabel, setLocalTrueLabel] = React.useState(trueLabel || '');
  const [localFalseLabel, setLocalFalseLabel] = React.useState(
    falseLabel || ''
  );

  React.useEffect(() => {
    setLocalTrueLabel(trueLabel || '');
  }, [trueLabel]);

  React.useEffect(() => {
    setLocalFalseLabel(falseLabel || '');
  }, [falseLabel]);

  const {
    env: {transaction},
  } = useExpressionEditorImplementationContext();

  const onFocus = React.useCallback(() => {
    transaction.begin();
  }, [transaction]);

  const handleTrueLabelBlur = React.useCallback(() => {
    onChange({...(meta || {}), trueLabel: localTrueLabel});
    transaction.commit();
  }, [meta, localTrueLabel, onChange, transaction]);

  const handleFalseLabelBlur = React.useCallback(() => {
    onChange({...(meta || {}), falseLabel: localFalseLabel});
    transaction.commit();
  }, [meta, localFalseLabel, onChange, transaction]);

  return (
    <>
      <Text id="boolean-on-label" width={49} colorVariant="secondary">
        On label
      </Text>
      <>
        <TextInput
          value={localTrueLabel}
          onChange={(e) => setLocalTrueLabel(e.target.value)}
          onFocus={onFocus}
          onBlur={handleTrueLabelBlur}
          data-automation-id="boolean-true-label"
          aria-labelledby="boolean-on-label"
        />
      </>
      <Text id="boolean-off-label" width={49} colorVariant="secondary">
        Off label
      </Text>
      <>
        <TextInput
          value={localFalseLabel}
          onChange={(e) => setLocalFalseLabel(e.target.value)}
          onFocus={onFocus}
          onBlur={handleFalseLabelBlur}
          data-automation-id="boolean-false-label"
          aria-labelledby="boolean-off-label"
        />
      </>
    </>
  );
};

export const plugin = Plugins.PrivilegedPlugin({
  icons: {
    Boolean: FieldSwitchIcon,
  },
  dataTypes: {
    Checkbox: CheckboxAliasType,
    ConditionValue: ConditionValueAliasType,

    Boolean: TypeImplementation.Alias({
      type: unionType,
      system: {
        displayName: 'Switch',
      },
      render: function RenderBoolean({expr, dispatch}) {
        let updatedUnionType = withSystem({labelVariant: 'hidden'}, unionType);

        // read overrides for True and False labels from type meta
        const type = getAnn(expr)?.type;
        const meta = type ? getMeta(type) : null;
        const {trueLabel, falseLabel} = parseLabelsFromMeta(meta);

        // use optionLabels meta to override the labels in Enum type render
        if (trueLabel || falseLabel) {
          const optionLabels: {[key: string]: string} = {};
          if (trueLabel) {
            optionLabels.True = trueLabel;
          }
          if (falseLabel) {
            optionLabels.False = falseLabel;
          }
          updatedUnionType = withMeta({optionLabels}, updatedUnionType);
        }

        const newExpr = setType(expr, updatedUnionType);
        return <ExpressionEditor expression={newExpr} dispatch={dispatch} />;
      },
      icon: TypeImplementation.IconByName(name(iconName('Boolean'))),
      metaUI: BooleanMetaUI,
      renderSummary: ({expr}: {expr: ContextualExpression}) => {
        // read overrides for True and False labels from type meta
        const type = getAnn(expr)?.type;
        const meta = type ? getMeta(type) : null;
        const {trueLabel, falseLabel} = parseLabelsFromMeta(meta);

        const union = ContextualExpressions.getUnion(stripAnn(expr));
        // If we don't have a Union expression resort back to DataTypeSummary
        // for handling the summary render
        if (!union) return <DataTypeSummary context={expr} />;
        const tag = union.tag;
        if (tag === 'True') {
          return <span>{trueLabel || 'True'}</span>;
        }
        if (tag === 'False') {
          return <span>{falseLabel || 'False'}</span>;
        }
        return <DataTypeSummary context={expr} />;
      },
    }),

    /**
     * A nominally unique type that under the hood is a predicate function;
     * that is a function that returns a Boolean.
     *
     * Using a nominally unique type for predicates prevents them from popping
     * up in unexpected places. This is because the type-system doesn’t see them
     * as functions and so it does not try to show calls to these functions in
     * binding menus.
     *
     * This type is accompanied by 2 functions defined further below:
     * - `Predicate` which constructs a `Predicate` value out of a regular
     *   predicate function.
     * - `Run Predicate` which given a `Predicate` value returns back the
     *   predicate function.
     */
    Predicate: TypeImplementation.Custom({
      params: ['a'],
      parseValue: (_) =>
        Result.Error(
          'Cannot use Literal expressions to create Predicate values.'
        ),
      render: () => null,
      icon: TypeImplementation.IconByName(name(iconName('Boolean'))),
    }),
  },

  values: {
    True: {
      envType: {
        exported: 'hidden',
        dataType: TBoolean,
      },
      expression: EUnion({
        tag: 'True',
        value: ERecord({}),
      }),
    },

    False: {
      envType: {
        exported: 'hidden',
        dataType: TBoolean,
      },
      expression: EUnion({
        tag: 'False',
        value: ERecord({}),
      }),
    },

    Every: {
      envType: {
        dataType: functionOf(TList(TBoolean), TBoolean),
        exported: 'hidden',
      },
      value: (list: Array<BooleanTag>): BooleanTag => {
        return toBooleanTag(!list.some((item) => item.tag === 'False'));
      },
    },

    Some: {
      envType: {
        dataType: functionOf(TList(TBoolean), TBoolean),
        exported: 'hidden',
      },
      value: (list: Array<BooleanTag>): BooleanTag => {
        return toBooleanTag(list.some((item) => item.tag === 'True'));
      },
    },

    Equals: {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'is'},
          functionOf(
            withSystem({isBindable: true}, TConditionValue),
            TPredicate(TBoolean)
          )
        ),
      },
      expression: EFunction({
        param: 'x',
        body: ECase({
          value: $('x'),
          cases: {
            True: EFunction({
              param: '_',
              body: _$('Boolean', 'Is true'),
            }),
            False: EFunction({
              param: '_',
              body: _$('Boolean', 'Is not true'),
            }),
          },
        }),
      }),
    },

    DoesNotEqual: {
      envType: {
        exported: 'visible',
        dataType: withSystem(
          {label: 'is not'},
          functionOf(
            withSystem({isBindable: true}, TConditionValue),
            TPredicate(TBoolean)
          )
        ),
      },
      expression: EFunction({
        param: 'x',
        body: ECase({
          value: $('x'),
          cases: {
            True: EFunction({
              param: '_',
              body: _$('Boolean', 'Is not true'),
            }),
            False: EFunction({
              param: '_',
              body: _$('Boolean', 'Is true'),
            }),
          },
        }),
      }),
    },

    'Is true': {
      envType: {
        exported: 'no',
        dataType: TPredicate(TBoolean),
      },
      expression: predicate(func(['boolean'], $('boolean'))),
    },

    'Is not true': {
      envType: {
        exported: 'no',
        dataType: TPredicate(TBoolean),
      },
      expression: predicate(
        func(
          ['boolean'],
          ECase({
            value: $('boolean'),
            cases: {
              True: func(['_'], False),
              False: func(['_'], True),
            },
          })
        )
      ),
    },

    Predicate: {
      envType: {
        dataType: forall(['a'], tyfun([tyfun([a], TBoolean)], TPredicate(a))),
        exported: 'hidden',
      },
      value: identity,
    },

    'Run Predicate': {
      envType: {
        dataType: forall(['a'], tyfun([TPredicate(a), a], TBoolean)),
        exported: 'hidden',
      },
      value: identity,
    },

    'To Text': {
      envType: {
        dataType: tyfun([TBoolean], TText),
        exported: 'visible',
        implicit: 'prefilledArgs',
      },
      expression: func(
        ['boolean value'],
        ECase({
          value: $('boolean value'),
          cases: {
            True: func(['_'], EText('True')),
            False: func(['_'], EText('False')),
          },
        })
      ),
    },

    'To Code Text': {
      envType: {
        dataType: tyfun([TBoolean], TText),
        exported: 'hidden',
        implicit: 'prefilledArgs',
      },
      expression: func(
        ['boolean value'],
        ECase({
          value: $('boolean value'),
          cases: {
            True: func(['_'], EText('true')),
            False: func(['_'], EText('false')),
          },
        })
      ),
    },

    Condition: operand(TBoolean),
  },
});
